<template>
  <div>
    <Head :title="`${$t('treatments.treatment')} #${data.id}`" />
    <h1 class="mb-8 text-3xl font-bold">
      <span v-if="viaVolunteer" class="text-gray-400">
        {{ data.applicant_id }}. {{ data.applicant_name }}
      </span>
      <Link v-else class="text-gray-400 hover:text-gray-600" :href="`/applicants/${data.applicant_id}/view`">
        {{ data.applicant_id }}. {{ data.applicant_name }}
      </Link>
      <span class="text-gray-400 font-medium">/ </span>
      <Link class="text-gray-400 hover:text-gray-600" :href="`/applicants/${data.applicant_id}/treatments/${data.treatment_id}/view`">{{ data.treatment_type_name }}</Link>
      <span class="text-gray-400 font-medium">/ </span>
      {{ $t('interventions.intervention') }}
      <div class="float-right">
        <Link :class="(!prev) ? 'disabled-button opacity-50' : ''" class="btn-indigo inline-flex items-center mr-1" :href="`/applicants/${data.applicant_id}/treatments/${data.treatment_id}/interventions/${prev}/view`">
        <icon name="cheveron-left-big" class="w-4 h-4 fill-current text-white" />
      </Link>
        <Link :class="(!next) ? 'disabled-button opacity-50' : ''" class="btn-indigo inline-flex items-center mr-2" :href="`/applicants/${data.applicant_id}/treatments/${data.treatment_id}/interventions/${next}/view`">
          <icon name="cheveron-right-big" class="w-4 h-4 fill-current text-white" />
        </Link></div>
    </h1>
    <trashed-message v-if="data.deleted_at" class="mb-6" @restore="restore"> {{ $t('applicants.trashed_message') }}</trashed-message>
    <div class="grid gap-4 lg:grid-cols-2 grid-cols-1">
      <detail-view :data="viewData">
        <template #default="item">
          <status-badge v-if="item.props.key === 'status'" class="flex" :status="item.props.value" />
          <span v-if="item.props.key === 'volunteer' && viaVolunteer">
            {{ item.props.value.name }}
          </span>
          <Link v-if="item.props.key === 'volunteer' && !viaVolunteer" :href="`/volunteers/${item.props.value.id}/view`" class="flex items-center focus:text-indigo-500 underline">{{ item.props.value.name }}</Link>
          <file-badge v-if="item.props.key === 'files'" :files="item.props.value" :intervention="data" :uploadUrl="uploadUrl" />
        </template>
      </detail-view>
      <note v-if="showInfo" :intervention="data" />
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TrashedMessage from '@/Shared/TrashedMessage'
import Note from '@/Pages/Interventions/Panels/Note'
import DetailView from '@/Shared/DetailView'
import StatusBadge from '@/Shared/StatusBadge'
import FileBadge from '@/Shared/FileBadge'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Head,
    Link,
    TrashedMessage,
    Note,
    DetailView,
    StatusBadge,
    FileBadge,
    Icon
  },
  layout: Layout,
  props: {
    data: Object,
    auth: Object,
    next: Number,
    prev: Number
  },
  computed:{
    viaVolunteer(){
      return this.auth.user.isVolunteer
    },
    showInfo(){
      if (this.$store.getters.can(this.data.treatment_type_name.toLowerCase()) || this.$store.getters.can('admin') || this.$store.getters.can('secretariat')) {
        return true
      }
      return false
    },
    uploadUrl() {
      return `/interventions/${this.data.id}/upload-file`
    },
    viewData(){
      return {
        items: [
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'date'),
            label: this.$t('interventions.date'),
            value: this.$filters.formatDate(this.data.date),
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'type'),
            label: this.$t('interventions.intervention_type'),
            value: this.data.intervention_type_name,
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'request'),
            label: this.$t('interventions.request'),
            value: this.data.requests.join(', ') + ((this.data.request_field) ? ' - ' + this.data.request_field : ''),
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'cooperation'),
            label: this.$t('interventions.cooperation'),
            value: this.data.cooperation_name,
          },
          {
            key: 'volunteer',
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'volunteer'),
            label: this.$t('interventions.volunteer'),
            value: {id: this.data.volunteer_id, name: this.data.volunteer_name},
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'employee'),
            label: this.$t('interventions.user'),
            value: this.data.user_name,
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'location'),
            label: this.$t('interventions.location'),
            value: this.data.location_name + ((this.data.location_field) ? ' - ' + this.data.location_field : ''),
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'send_field'),
            label: this.$t('interventions.send_type'),
            value: this.data.send_field_value,
          },
          {
            hide: !this.$permissions.getPermission(this.data.treatment_type_name, 'seen'),
            label: this.$t('interventions.seen'),
            value: this.data.seen_value,
          },
          {
            key: 'files',
            label: this.$t('global.files'),
            value: this.data.files,
          },
        ],
        updateLink: `/applicants/${this.data.applicant_id}/treatments/${this.data.treatment_id}/interventions/${this.data.id}/edit`,
        header: {
          title: 'Intervention Information',
          text: 'Intervention details and documentation.',
        },
      }
    },
  },
}
</script>
<style>
.disabled-button {
  pointer-events: none;

}
</style>
